// =============================================================================
// Tenant admin Dashboard
//
// Single page that replaces the old Dashboard + Analytics pair. Covers:
//   • header — plan badge, monthly cost, window selector (7/14/30/90/1y +
//     Custom), auto-refresh toggle, manual Refresh metrics
//   • KPIs — 4 unified cards: Active Users / Trips / Storage / AI Calls.
//     Each shows current value, plan limit (if any), utilization gauge,
//     prior-period delta, and a tiny sparkline. The old "Plan Limits
//     Utilization" row is folded into these cards.
//   • chart — Daily Active Users over the selected window
//   • engage — avg daily active, trips/user, AI calls/user
//   • feature — feature usage breakdown by category, top 5, unused
//   • bulletin — bulletin-board interest stats (only shown when populated)
//
// Refresh metrics calls usePlatformStore.refreshSelectedTenant which updates
// selectedTenant in place WITHOUT toggling the global loading flag, so the
// page re-renders without flashing through TenantManagePage's spinner.
// =============================================================================

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  Users, MapPin, Sparkles, HardDrive, TrendingUp, Clock, Heart, Newspaper,
  RefreshCw, BarChart3, Zap, Brain, Wallet, UsersRound, AlertCircle, Lock,
  CalendarRange, ArrowUp, ArrowDown, ChevronDown, BookOpen, EyeOff,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePlatformStore } from '@/lib/store';
import { fetchUsageMetrics, fetchBulletinInterestStats, triggerUsageSync, fetchPlanFeatureDefaults, fetchDashboardExtras, fetchTenantJournals, type BulletinInterestStats, type TopUserEntry, type AiErrorStats, type AiByFeatureEntry, type AiActiveUserEntry, type AppTenantJournal } from '@/lib/api';
import { MODERATION_REASONS, reasonMeta } from '@/lib/moderationCodes';
import { FEATURE_META } from '@/lib/constants';
import { BuddyTripsAnalyticsCard } from '@/components/dashboard/BuddyTripsAnalyticsCard';
import { BuddyAbuseAnalyticsCard } from '@/components/dashboard/BuddyAbuseAnalyticsCard';
import { AiCallsModal } from './AiCallsModal';
import { PlanBadge, Spinner } from '@/components/shared/UIComponents';
import { formatCompact, formatStorage, formatCurrency, formatRelative } from '@/utils/helpers';
import type { UsageMetric, TenantPlan, PlanName, FeatureUsageEntry, PlanFeatureDefault } from '@/types';

// ── Types ────────────────────────────────────────────────────────────────────

type PresetDays = 7 | 14 | 30 | 90 | 365;
type WindowSelection =
  | { kind: 'preset'; days: PresetDays }
  | { kind: 'custom'; from: string; to: string };

type AutoRefreshInterval = 0 | 30 | 60 | 300;

// ── Feature category styling ────────────────────────────────────────────────
const CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string; icon: ReactNode }> = {
  core:          { label: 'Core',            color: 'text-blue-600 dark:text-blue-400',    bg: 'bg-blue-500',    icon: <Zap size={14} /> },
  ai:            { label: 'Smart Features',  color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-500', icon: <Brain size={14} /> },
  budget:        { label: 'Budget',          color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500', icon: <Wallet size={14} /> },
  collaboration: { label: 'Collaboration',   color: 'text-amber-600 dark:text-amber-400',  bg: 'bg-amber-500',   icon: <UsersRound size={14} /> },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const PRESET_LABEL: Record<PresetDays, string> = {
  7: '7d', 14: '14d', 30: '30d', 90: '90d', 365: '1y',
};

function isoDate(d: Date): string { return d.toISOString().split('T')[0]; }
function addDays(d: Date, n: number): Date { return new Date(d.getTime() + n * 86400000); }

function daysBetween(from: string, to: string): number {
  return Math.max(1, Math.round(
    (new Date(to).getTime() - new Date(from).getTime()) / 86400000
  ) + 1);
}

// Resolve a WindowSelection to absolute date strings + prior period.
function resolveRange(sel: WindowSelection): {
  from: string; to: string;
  priorFrom: string; priorTo: string;
  days: number;
} {
  if (sel.kind === 'preset') {
    const today = new Date();
    const to = isoDate(today);
    const from = isoDate(addDays(today, -(sel.days - 1)));
    const priorTo = isoDate(addDays(today, -sel.days));
    const priorFrom = isoDate(addDays(today, -(sel.days * 2 - 1)));
    return { from, to, priorFrom, priorTo, days: sel.days };
  }
  // custom — same-length prior window
  const days = daysBetween(sel.from, sel.to);
  const priorTo = isoDate(addDays(new Date(sel.from), -1));
  const priorFrom = isoDate(addDays(new Date(sel.from), -days));
  return { from: sel.from, to: sel.to, priorFrom, priorTo, days };
}

// Sum a metric over an array of UsageMetric rows.
function sumMetric<K extends keyof UsageMetric>(rows: UsageMetric[], key: K): number {
  let s = 0;
  for (const r of rows) {
    const v = r[key] as unknown;
    if (typeof v === 'number') s += v;
    else if (typeof v === 'string') s += Number(v) || 0;
  }
  return s;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function TenantDashboardPage({ tenantId }: { tenantId: string }) {
  const { selectedTenant: tenant, refreshSelectedTenant } = usePlatformStore();
  const [selection, setSelection] = useState<WindowSelection>({ kind: 'preset', days: 14 });
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshInterval>(0);
  const [metrics, setMetrics] = useState<UsageMetric[]>([]); // current period
  const [priorMetrics, setPriorMetrics] = useState<UsageMetric[]>([]); // prior period
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [interestStats, setInterestStats] = useState<BulletinInterestStats | null>(null);
  const [planDefaults, setPlanDefaults] = useState<PlanFeatureDefault[]>([]);
  const [topUsers, setTopUsers] = useState<TopUserEntry[]>([]);
  const [aiErrors, setAiErrors] = useState<AiErrorStats | null>(null);
  const [aiByFeature, setAiByFeature] = useState<AiByFeatureEntry[]>([]);
  const [aiActiveUsers, setAiActiveUsers] = useState<AiActiveUserEntry[]>([]);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  // Stories KPI feed — pulled from the same edge fn that powers the
  // Stories page so the counts agree exactly. We keep the raw rows in
  // state because we derive multiple stats client-side (published this
  // week, hidden count, total likes) and a single fetch is cheaper than
  // round-tripping each metric separately.
  const [storyRows, setStoryRows] = useState<AppTenantJournal[] | null>(null);

  const range = useMemo(() => resolveRange(selection), [selection]);

  // Fetch both current + prior period in one combined query, then split.
  const loadMetrics = async () => {
    const series = await fetchUsageMetrics(tenantId, { from: range.priorFrom, to: range.to });
    const cur: UsageMetric[] = [];
    const prev: UsageMetric[] = [];
    for (const m of series) {
      if (m.date >= range.from) cur.push(m);
      else prev.push(m);
    }
    setMetrics(cur);
    setPriorMetrics(prev);
  };

  // Window selector or tenant change → refetch.
  useEffect(() => {
    setLoading(true);
    loadMetrics().catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, range.from, range.to]);

  // Top users + AI error stats + AI by-feature breakdown — one fetch
  // covers all three; loaded on window change.
  useEffect(() => {
    fetchDashboardExtras(tenantId, range.from, range.to)
      .then(r => {
        setTopUsers(r.top_users);
        setAiErrors(r.ai_errors);
        setAiByFeature(r.ai_by_feature || []);
        setAiActiveUsers(r.ai_active_users || []);
      })
      .catch(() => { /* non-fatal — these panels just won't render */ });
  }, [tenantId, range.from, range.to]);

  useEffect(() => {
    fetchBulletinInterestStats(tenantId).then(setInterestStats).catch(() => {});
  }, [tenantId]);

  // Stories KPI — read-only, untouched by the window selector so the
  // tile always shows "this week" regardless of what the dashboard
  // selector is set to. fetchTenantJournals caps the list at 200 rows
  // which is the same set the Stories page operates on; that's plenty
  // headroom for any "this week" derivation.
  useEffect(() => {
    fetchTenantJournals(tenantId).then(setStoryRows).catch(() => setStoryRows([]));
  }, [tenantId]);

  // Plan feature defaults drive the lock-icon and limit display.
  const planId = (tenant?.plan as TenantPlan | undefined)?.id;
  useEffect(() => {
    if (!planId) return;
    fetchPlanFeatureDefaults(planId).then(setPlanDefaults).catch(() => {});
  }, [planId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await triggerUsageSync(tenantId);
      if (r.failed > 0) {
        toast.warning(`Sync ran with ${r.failed} failure${r.failed === 1 ? '' : 's'}.`);
      } else {
        toast.success('Metrics refreshed.');
      }
      await Promise.all([loadMetrics(), refreshSelectedTenant(tenantId)]);
      fetchBulletinInterestStats(tenantId).then(setInterestStats).catch(() => {});
    } catch (e) {
      toast.error(`Refresh failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refresh: silent variant — no toast, no refresh-spinner flash.
  const silentRefresh = useRef<() => void>(() => {});
  silentRefresh.current = async () => {
    try {
      await triggerUsageSync(tenantId);
      await Promise.all([loadMetrics(), refreshSelectedTenant(tenantId)]);
    } catch {
      // Auto-refresh failures are silent — user can click manual Refresh.
    }
  };
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => silentRefresh.current(), autoRefresh * 1000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  if (!tenant) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  const plan = tenant.plan as TenantPlan | undefined;
  const planName = (plan?.name || 'free') as PlanName;
  const monthlyCost = (plan?.price_per_seat_monthly || 0) * (tenant.current_user_count || 1);

  // Aggregations — current and prior period.
  const totalTrips    = sumMetric(metrics, 'trips_created');
  const totalAI       = sumMetric(metrics, 'ai_requests');
  const priorTrips    = sumMetric(priorMetrics, 'trips_created');
  const priorAI       = sumMetric(priorMetrics, 'ai_requests');
  const avgUsers      = metrics.length ? Math.round(sumMetric(metrics, 'active_users') / metrics.length) : 0;
  const peakDay       = metrics.length ? metrics.reduce((max, m) => m.active_users > max.active_users ? m : max, metrics[0]) : null;

  // Storage delta: oldest metric in window vs current snapshot.
  const oldestStorageMb = metrics.length ? Number(metrics[0].storage_used_mb) : Number(tenant.storage_used_mb);
  const currentStorageMb = Number(tenant.storage_used_mb);
  const storageDelta = currentStorageMb - oldestStorageMb;

  // Pad current period to N bars for the chart and sparklines.
  const paddedMetrics = useMemo<UsageMetric[]>(() => {
    const out: UsageMetric[] = [];
    const byDate = new Map(metrics.map(m => [m.date, m]));
    const fromMs = new Date(range.from).getTime();
    for (let i = 0; i < range.days; i++) {
      const d = isoDate(new Date(fromMs + i * 86400000));
      const existing = byDate.get(d);
      out.push(existing ?? {
        id: `pad-${d}`, tenant_id: tenantId, date: d,
        active_users: 0, trips_created: 0, ai_requests: 0,
        storage_used_mb: 0, feature_usage_data: [],
      });
    }
    return out;
  }, [metrics, range.from, range.days, tenantId]);

  // Sparkline data per metric. Uses padded values so day count matches.
  const sparkUsers   = paddedMetrics.map(m => m.active_users);
  const sparkTrips   = paddedMetrics.map(m => m.trips_created);
  const sparkAI      = paddedMetrics.map(m => m.ai_requests);
  const sparkStorage = paddedMetrics.map(m => Number(m.storage_used_mb) || 0);

  // Feature usage from latest synced metric row.
  const latestMetric = metrics.length ? metrics[metrics.length - 1] : null;
  const featureUsageRaw: FeatureUsageEntry[] = latestMetric?.feature_usage_data || [];

  // ── Foldable Feature Usage card + sort + show-unused state ──
  // Tenant admins asked to be able to see at a glance "what's in our
  // plan vs what are we actually using" without scrolling through
  // the full category panel. Header shows "X of N used" where N is
  // the plan-enabled feature count and X is the count actually
  // exercised in the current window. Body defaults CLOSED so the
  // tab stays scannable on first paint; the toolbar inside the body
  // lets the user re-sort the list and surface plan features that
  // had zero uses in the window.
  const [featureUsageOpen, setFeatureUsageOpen] = useState(false);
  const [featureSort, setFeatureSort] = useState<'most-used' | 'least-used' | 'a-z' | 'last-used'>('most-used');
  const [showUnused, setShowUnused] = useState(false);

  const planByKey = useMemo(() => {
    const map = new Map<string, PlanFeatureDefault>();
    planDefaults.forEach(d => map.set(d.feature_key, d));
    return map;
  }, [planDefaults]);

  // Plan-size denominator + header counter. planSize counts enabled
  // features in the tenant's plan; usedInPlan counts how many of
  // those were exercised at least once in the window. Falls back to
  // the raw used count when plan defaults haven't loaded.
  const planEnabledKeys = useMemo(() =>
    new Set(planDefaults.filter(d => d.is_enabled).map(d => d.feature_key)),
    [planDefaults],
  );
  const planSize = planEnabledKeys.size;
  const usedInPlan = useMemo(() => {
    const used = new Set(featureUsageRaw.filter(f => f.total_usage > 0).map(f => f.feature_key));
    return planSize > 0
      ? Array.from(used).filter(k => planEnabledKeys.has(k)).length
      : used.size;
  }, [featureUsageRaw, planSize, planEnabledKeys]);

  // When showUnused is on, append zero-rows for every plan-enabled
  // feature that didn't appear in the latest metric — so the user
  // sees the whole plan with usage data overlaid. Otherwise this is
  // a passthrough.
  const featureUsage: FeatureUsageEntry[] = useMemo(() => {
    if (!showUnused) return featureUsageRaw;
    const present = new Set(featureUsageRaw.map(f => f.feature_key));
    const extras: FeatureUsageEntry[] = [];
    for (const d of planDefaults) {
      if (!d.is_enabled || present.has(d.feature_key)) continue;
      const meta = FEATURE_META[d.feature_key];
      extras.push({
        feature_key:  d.feature_key,
        feature_name: meta?.label || d.feature_key,
        category:     (meta?.category || 'other').toLowerCase(),
        // Synthesize a sensible limit_type for the zero-row: features
        // with hasLimit=true are 'count', everything else 'boolean'.
        // Display panels treat zero-usage rows as the muted "unused"
        // case so the exact value doesn't affect rendering — this
        // just satisfies the type.
        limit_type:   meta?.hasLimit ? 'count' : 'boolean',
        total_usage:  0,
        unique_users: 0,
        last_used_at: null,
      });
    }
    return [...featureUsageRaw, ...extras];
  }, [featureUsageRaw, showUnused, planDefaults]);

  const formatLimit = (limit: number | null | undefined): string => {
    if (limit === null || limit === undefined) return '∞';
    if (limit === -1) return '∞';
    return String(limit);
  };

  const byCategory = useMemo(() => {
    const map: Record<string, FeatureUsageEntry[]> = {};
    featureUsage.forEach(f => {
      const cat = (f.category || 'general').toLowerCase();
      if (!map[cat]) map[cat] = [];
      map[cat].push(f);
    });
    // Sort within each category according to the user's choice.
    // Defaults to 'most-used' which preserves the historical ordering.
    Object.values(map).forEach(arr => arr.sort((a, b) => {
      if (featureSort === 'least-used') return a.total_usage - b.total_usage;
      if (featureSort === 'a-z')        return a.feature_name.localeCompare(b.feature_name);
      if (featureSort === 'last-used') {
        // Recent first; nulls sink to the bottom.
        const aT = a.last_used_at ? Date.parse(a.last_used_at) : 0;
        const bT = b.last_used_at ? Date.parse(b.last_used_at) : 0;
        return bT - aT;
      }
      return b.total_usage - a.total_usage;
    }));
    return map;
  }, [featureUsage, featureSort]);

  const categoryTotals = useMemo(() => {
    const totals: Record<string, { usage: number; users: number; count: number }> = {};
    featureUsage.forEach(f => {
      const cat = (f.category || 'general').toLowerCase();
      if (!totals[cat]) totals[cat] = { usage: 0, users: 0, count: 0 };
      totals[cat].usage += f.total_usage;
      totals[cat].users  = Math.max(totals[cat].users, f.unique_users);
      totals[cat].count += 1;
    });
    return totals;
  }, [featureUsage]);

  const sortedByUsage = useMemo(() =>
    [...featureUsage].sort((a, b) => b.total_usage - a.total_usage),
    [featureUsage],
  );
  const topFeatures = sortedByUsage.slice(0, 5);
  // Unused = no usage in the window AND the plan actually enables the
  // feature. Without the is_enabled gate, plan-disabled features (Advanced
  // Roles, Visual Context, etc.) inflated the "Unused" count and read as
  // "missing usage" when they're actually unavailable. Tenants saw "18 of
  // 38" when the real number of plan features they could touch was much
  // lower. Falls back to all-zero-usage features when planDefaults hasn't
  // loaded so the panel doesn't temporarily render blank during the first
  // paint.
  const unusedFeatures = featureUsage.filter(f => {
    if (f.total_usage !== 0) return false;
    if (planDefaults.length === 0) return true; // pre-load fallback
    return planByKey.get(f.feature_key)?.is_enabled === true;
  });
  const totalFeatureUsage = featureUsage.reduce((s, f) => s + f.total_usage, 0);
  const visibleCategories = activeCategory
    ? { [activeCategory]: byCategory[activeCategory] || [] }
    : byCategory;

  const windowLabel = selection.kind === 'preset'
    ? PRESET_LABEL[selection.days]
    : `${selection.from} → ${selection.to}`;

  // ── Stories KPI derivation ─────────────────────────────────────────
  // Hard-pinned to a rolling 7-day window so the tile reads the same
  // regardless of the dashboard selector. We count published-this-week
  // (the "is the public feed staying fresh?" health signal) and the
  // current hidden count (the "are we actively moderating?" signal).
  // Memoized so the derivations don't re-run on unrelated state
  // changes elsewhere in this page.
  const storiesKpi = useMemo(() => {
    if (!storyRows) return null;
    const weekAgo = Date.now() - 7 * 86_400_000;
    let publishedThisWeek = 0;
    let hidden            = 0;
    let totalLikes        = 0;
    for (const j of storyRows) {
      totalLikes += j.feed_likes_count ?? 0;
      if (j.tenant_hidden_at) hidden++;
      const pub = j.published_at ? Date.parse(j.published_at) : NaN;
      if (!Number.isNaN(pub) && pub >= weekAgo) publishedThisWeek++;
    }
    return { publishedThisWeek, hidden, totalLikes, total: storyRows.length };
  }, [storyRows]);

  // Phase 3C — moderation reason breakdown. Aggregates the CURRENT hide
  // state across every row (not historical hides — the audit log has
  // those if a deeper view is ever needed). Counts only rows that have
  // a code; pre-3C historical hides (code = null) are bucketed under
  // 'other' so they aren't silently dropped from the trend.
  // Reused by the stacked-bar card below.
  const moderationReasons = useMemo(() => {
    if (!storyRows) return null;
    const counts: Record<string, number> = {};
    for (const code of MODERATION_REASONS.map(r => r.code)) counts[code] = 0;
    let totalHidden = 0;
    for (const j of storyRows) {
      if (!j.tenant_hidden_at) continue;
      totalHidden++;
      const code = j.tenant_hidden_reason_code || 'other';
      counts[code] = (counts[code] ?? 0) + 1;
    }
    return { counts, totalHidden };
  }, [storyRows]);

  return (
    <div className="space-y-6">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-surface-900">Dashboard</h1>
          <div className="flex items-center gap-3 mt-1.5">
            <PlanBadge plan={planName} label={plan?.display_name} />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <WindowSelector value={selection} onChange={setSelection} />
          <AutoRefreshToggle value={autoRefresh} onChange={setAutoRefresh} />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            title="Pull the latest counts now"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surface-100 hover:bg-surface-200 text-surface-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh metrics'}
          </button>
          {monthlyCost > 0 && (
            <div className="text-right ml-1">
              <div className="text-xs text-surface-500">Monthly cost</div>
              <div className="text-lg font-bold text-surface-900">{formatCurrency(monthlyCost)}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Unified KPI cards ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<Users size={16} />}
          label="Active Users"
          value={tenant.current_user_count}
          limit={tenant.max_users || plan?.max_users || null}
          spark={sparkUsers}
          delay={0}
          subtitle={`Avg ${avgUsers}/day in window`}
          tooltip="Active seats currently consumed in tenant_users. The bar shows utilization vs the plan's max_users limit; sparkline shows distinct daily-active users over the window."
        />
        <KpiCard
          icon={<MapPin size={16} />}
          label={`Trips · ${windowLabel}`}
          value={totalTrips}
          limit={plan?.max_trips_per_month ?? null}
          delta={priorTrips > 0 ? Math.round(((totalTrips - priorTrips) / priorTrips) * 100) : (totalTrips > 0 ? 100 : 0)}
          deltaLabel={selection.kind === 'preset' ? `vs prior ${PRESET_LABEL[selection.days]}` : 'vs prior period'}
          spark={sparkTrips}
          delay={50}
          tooltip="Total new trip rows created during the window — includes both newly built itineraries AND clones of curated/template trips. A user clicking 'Save this trip' on a template counts as a created trip."
          subtitle="incl. clones"
        />
        <KpiCard
          icon={<HardDrive size={16} />}
          label="Storage"
          value={currentStorageMb}
          formatValue={(v) => formatStorage(v)}
          limit={tenant.storage_limit_mb || plan?.max_storage_mb || null}
          formatLimit={(v) => formatStorage(v)}
          delta={oldestStorageMb > 0 ? Math.round((storageDelta / oldestStorageMb) * 100) : (storageDelta > 0 ? 100 : 0)}
          deltaLabel={`+${formatStorage(Math.max(0, storageDelta))} in window`}
          spark={sparkStorage}
          delay={100}
          tooltip="All user-uploaded content for this tenant: receipts, scanned documents, boarding-pass photos, foodie photos. Excludes shared platform caches and avatars sourced from external CDNs."
        />
        <KpiCard
          icon={<Sparkles size={16} />}
          label={`AI Calls · ${windowLabel}`}
          value={totalAI}
          formatValue={formatCompact}
          delta={priorAI > 0 ? Math.round(((totalAI - priorAI) / priorAI) * 100) : (totalAI > 0 ? 100 : 0)}
          deltaLabel={
            aiErrors && aiErrors.error_count > 0
              ? `${aiErrors.error_rate}% errors · ${selection.kind === 'preset' ? `vs prior ${PRESET_LABEL[selection.days]}` : 'vs prior period'}`
              : selection.kind === 'preset' ? `vs prior ${PRESET_LABEL[selection.days]}` : 'vs prior period'
          }
          spark={sparkAI}
          delay={150}
          accent="violet"
          tooltip="Click to drill into per-feature breakdown, daily trend, top users."
          onClick={() => setAiModalOpen(true)}
        />
      </div>

      {/* ── Chart + Engagement ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-panel p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-surface-900">Daily Active Users · {windowLabel}</h3>
            {peakDay && peakDay.active_users > 0 && (
              <span className="text-[11px] text-surface-500">
                Peak: <span className="font-medium text-surface-900">{peakDay.active_users}</span> on {peakDay.date}
              </span>
            )}
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <>
              <div className="flex items-end gap-[3px] h-40">
                {paddedMetrics.map((m, i) => {
                  const max = Math.max(...paddedMetrics.map(x => x.active_users), 1);
                  const h = (m.active_users / max) * 100;
                  const isEmpty = m.active_users === 0;
                  return (
                    <div key={i} className="flex-1 group relative">
                      <div className={`w-full rounded-t-sm transition-colors cursor-default ${
                        isEmpty
                          ? 'bg-surface-200 hover:bg-surface-300'
                          : 'bg-brand-500/70 hover:bg-brand-500'
                      }`}
                        style={{ height: `${Math.max(2, h)}%`, minHeight: '2px' }} />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                        <div className="bg-surface-0 border border-surface-300 shadow-lg rounded-xl px-3 py-2 text-xs whitespace-nowrap">
                          <div className="text-surface-900 font-medium">{m.date}</div>
                          <div className="text-surface-500">{m.active_users} users · {m.trips_created} trips</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-surface-500">{paddedMetrics[0]?.date}</span>
                <span className="text-[10px] text-surface-500">{paddedMetrics[paddedMetrics.length - 1]?.date}</span>
              </div>
            </>
          )}
        </div>

        <div className="space-y-4">
          <div className="glass-panel p-5">
            <h3 className="text-sm font-semibold text-surface-900 mb-3">Engagement</h3>
            <div className="space-y-3">
              <MiniRow icon={<TrendingUp size={14} />} label="Avg. daily active" value={avgUsers} />
              <MiniRow icon={<MapPin size={14} />} label="Trips per user" value={tenant.current_user_count ? (totalTrips / tenant.current_user_count).toFixed(1) : '0'} />
              <MiniRow icon={<Clock size={14} />} label="AI calls / user" value={tenant.current_user_count ? (totalAI / tenant.current_user_count).toFixed(1) : '0'} />
            </div>
          </div>

          {/* Stories KPI tile — bridges the Dashboard to the new
              Stories tab. Surfaces "is the public feed staying fresh?"
              (published this week) and "are we actively moderating?"
              (hidden count) so the operator catches anomalies without
              having to dive into the Stories page. Header doubles as a
              link to the Stories tab. */}
          {/* Moderation reasons — Phase 3C stacked-bar of WHAT'S
              currently hidden, grouped by canonical category. Sits
              above the Stories KPI so the operator sees the "why" before
              the "how many." Only renders when there's at least one
              hidden row to chart so quiet tenants don't get an empty
              bar. The bar uses each reason's brand chip color so it
              matches the chip rendering on the Stories table. */}
          {moderationReasons && moderationReasons.totalHidden > 0 && (
            <div className="glass-panel p-5">
              <h3 className="text-sm font-semibold text-surface-900 mb-3 flex items-center gap-2">
                <EyeOff size={14} className="text-amber-500" />
                Hide reasons
                <span className="text-[10px] text-surface-400 font-normal ml-auto">
                  {moderationReasons.totalHidden} hidden total
                </span>
              </h3>
              {/* Stacked bar. Each segment is a flex-grow proportional
                  to its count. Tooltips show "category · count" so a
                  hover gives the exact number when the segments are
                  small. */}
              <div className="flex h-2 w-full rounded-full overflow-hidden bg-surface-100 mb-3">
                {MODERATION_REASONS.map(r => {
                  const c = moderationReasons.counts[r.code] ?? 0;
                  if (c === 0) return null;
                  // Use inline flex-grow because tailwind can't express
                  // dynamic grow ratios. Numeric grow = exact count.
                  return (
                    <div
                      key={r.code}
                      className={`${r.chipBg.replace('/15', '')} h-full`}
                      style={{ flexGrow: c }}
                      title={`${r.label}: ${c}`}
                    />
                  );
                })}
              </div>
              {/* Legend with counts. Two-column so 7 categories fit
                  cleanly even on narrower right-column widths. */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {MODERATION_REASONS.map(r => {
                  const c = moderationReasons.counts[r.code] ?? 0;
                  if (c === 0) return null;
                  return (
                    <div key={r.code} className="flex items-center gap-1.5 text-[11px]">
                      <span className={`w-2 h-2 rounded-full ${r.chipBg.replace('/15', '')}`} />
                      <span className="text-surface-600 truncate">{r.label}</span>
                      <span className="text-surface-900 font-semibold tabular-nums ml-auto">{c}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {storiesKpi && (storiesKpi.total > 0 || storiesKpi.publishedThisWeek > 0) && (
            <Link
              to={`/admin/tenants/${tenantId}/manage/stories`}
              className="glass-panel p-5 block hover:bg-overlay/[0.02] transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                  <BookOpen size={14} className="text-brand-500" />
                  Stories
                </h3>
                <span className="text-[10px] text-surface-400">View all ›</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-xl font-bold text-surface-900 tabular-nums">{storiesKpi.publishedThisWeek}</div>
                  <div className="text-[10px] text-surface-500 leading-tight mt-0.5">Published<br/>this week</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-surface-900 tabular-nums flex items-center gap-1">
                    {storiesKpi.hidden}
                    {storiesKpi.hidden > 0 && <EyeOff size={12} className="text-amber-500" />}
                  </div>
                  <div className="text-[10px] text-surface-500 leading-tight mt-0.5">Currently<br/>hidden</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-surface-900 tabular-nums flex items-center gap-1">
                    {storiesKpi.totalLikes}
                    <Heart size={12} className="text-rose-500" />
                  </div>
                  <div className="text-[10px] text-surface-500 leading-tight mt-0.5">Total<br/>likes</div>
                </div>
              </div>
            </Link>
          )}

          {topUsers.length > 0 && (
            <div className="glass-panel p-5">
              <h3 className="text-sm font-semibold text-surface-900 mb-3">Top Users · {windowLabel}</h3>
              <div className="space-y-2">
                {topUsers.map((u, i) => {
                  const initial = (u.full_name?.[0] || u.email?.[0] || '?').toUpperCase();
                  const display = u.full_name?.trim() || u.email || u.user_id.slice(0, 8);
                  return (
                    <div key={u.user_id} className="flex items-center gap-2.5">
                      <span className="text-[10px] font-bold text-surface-400 w-3 text-right">{i + 1}</span>
                      <div className="w-7 h-7 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-500 text-[11px] font-bold shrink-0">
                        {initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-surface-900 truncate">{display}</div>
                        <div className="text-[10px] text-surface-500">{u.ai_calls} AI · {u.trips_touched} trips</div>
                      </div>
                      <span className="text-xs font-semibold text-surface-900">{u.total_activity}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Feature usage section ───────────────────────────────────────── */}
      {featureUsage.length > 0 ? (
        <>
          {/* Category summary cards (clickable filters) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(CATEGORY_CONFIG).map(([catKey, cfg]) => {
              const t = categoryTotals[catKey];
              const isActive = activeCategory === catKey;
              return (
                <button key={catKey} onClick={() => setActiveCategory(isActive ? null : catKey)}
                  className={`glass-panel p-4 text-left transition-all ${isActive ? 'ring-2 ring-brand-500' : 'hover:bg-overlay/[0.02]'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={cfg.color}>{cfg.icon}</span>
                    <span className="text-xs font-medium text-surface-500">{cfg.label}</span>
                  </div>
                  <div className="text-lg font-bold text-surface-900">{formatCompact(t?.usage || 0)}</div>
                  <div className="text-[10px] text-surface-500">{t?.count || 0} features · {t?.users || 0} users</div>
                </button>
              );
            })}
          </div>

          {/* Feature usage by category — foldable. The header always
              renders with the "X of N used" counter; the body (toolbar
              + category list) only renders when open. */}
          <div className="glass-panel p-5">
            <div className="flex items-center justify-between gap-3">
              {/* Header is the entire left side — click anywhere on it
                  to toggle. Counter chip surfaces "used vs available"
                  at a glance even when the body is closed. */}
              <button
                type="button"
                onClick={() => setFeatureUsageOpen(o => !o)}
                className="flex items-center gap-2 text-left min-w-0 flex-1"
                aria-expanded={featureUsageOpen}
              >
                <ChevronDown
                  size={14}
                  className={`text-surface-500 shrink-0 transition-transform ${featureUsageOpen ? '' : '-rotate-90'}`}
                />
                <BarChart3 size={16} className="text-brand-500 shrink-0" />
                <h3 className="text-sm font-semibold text-surface-900 truncate">Feature Usage</h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold uppercase tracking-wider shrink-0">
                  {usedInPlan} of {planSize || featureUsageRaw.length} used
                </span>
              </button>
              {activeCategory && featureUsageOpen && (
                <button onClick={() => setActiveCategory(null)}
                  className="text-xs text-brand-500 hover:text-brand-600 shrink-0">Show All</button>
              )}
            </div>

            {featureUsageOpen && (
              <>
                {/* Toolbar: sort dropdown + show-unused toggle. Sits
                    between the foldable header and the categorized
                    list. The sort applies WITHIN each category so the
                    category grouping stays intact. */}
                <div className="flex items-center gap-3 mt-4 mb-3 flex-wrap">
                  <label className="flex items-center gap-1.5 text-xs text-surface-600">
                    <span className="font-semibold uppercase tracking-wider text-[10px]">Sort</span>
                    <select
                      value={featureSort}
                      onChange={(e) => setFeatureSort(e.target.value as typeof featureSort)}
                      className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-white text-surface-900"
                    >
                      <option value="most-used">Most used</option>
                      <option value="least-used">Least used</option>
                      <option value="a-z">A → Z</option>
                      <option value="last-used">Last used</option>
                    </select>
                  </label>
                  {planSize > 0 && (
                    <label className="flex items-center gap-1.5 text-xs text-surface-600 cursor-pointer select-none ml-auto">
                      <input
                        type="checkbox"
                        checked={showUnused}
                        onChange={(e) => setShowUnused(e.target.checked)}
                        className="w-3.5 h-3.5"
                      />
                      Show unused plan features
                    </label>
                  )}
                </div>

                <div className="space-y-5 max-h-[520px] overflow-y-auto pr-1 scrollbar-thin">
              {Object.entries(visibleCategories).map(([catKey, items]) => {
                const cfg = CATEGORY_CONFIG[catKey] || { label: catKey, color: 'text-surface-600', bg: 'bg-surface-500', icon: null };
                const catMax = Math.max(...items.map(f => f.total_usage), 1);
                return (
                  <div key={catKey}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={cfg.color}>{cfg.icon}</span>
                      <span className="text-xs font-semibold text-surface-600 uppercase tracking-wider">{cfg.label}</span>
                      <span className="text-[10px] text-surface-400">({items.length})</span>
                    </div>
                    <div className="space-y-2">
                      {items.map(f => {
                        const pct = catMax > 0 ? Math.round((f.total_usage / catMax) * 100) : 0;
                        const adoptionPct = tenant.current_user_count > 0
                          ? Math.round((f.unique_users / tenant.current_user_count) * 100)
                          : 0;
                        const planDef = planByKey.get(f.feature_key);
                        const isLocked = planDef ? !planDef.is_enabled : false;
                        const planLimit = planDef?.limit_value ?? null;
                        const limitPct = planLimit && planLimit > 0
                          ? Math.min(100, Math.round((f.total_usage / planLimit) * 100))
                          : pct;
                        const overLimit = planLimit && planLimit > 0 && f.total_usage > planLimit;
                        return (
                          <div key={f.feature_key} className={`group ${isLocked ? 'opacity-60' : ''}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1.5 min-w-0">
                                {isLocked && (
                                  <Lock size={11} className="text-surface-400 shrink-0"
                                    aria-label="Not included in this plan" />
                                )}
                                <span className="text-sm text-surface-700 truncate">{f.feature_name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs shrink-0">
                                <span className="text-surface-500">{f.unique_users} user{f.unique_users !== 1 ? 's' : ''}</span>
                                <span className={`font-medium ${overLimit ? 'text-red-600 dark:text-red-400' : 'text-surface-900'}`}>
                                  {f.total_usage.toLocaleString()}
                                  {planLimit !== null && (
                                    <span className="text-surface-500 font-normal"> / {formatLimit(planLimit)}</span>
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-surface-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${
                                  overLimit ? 'bg-red-500' : isLocked ? 'bg-surface-400' : cfg.bg
                                }`}
                                  style={{ width: `${limitPct}%`, opacity: f.total_usage === 0 ? 0.3 : 0.7 }} />
                              </div>
                              <span className="text-[10px] text-surface-400 w-10 text-right">{adoptionPct}%</span>
                            </div>
                            {f.last_used_at && (
                              <div className="text-[10px] text-surface-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                Last used {formatRelative(f.last_used_at)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
                </div>
              </>
            )}
          </div>

          {/* Top 5 + Unused */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="glass-panel p-5">
              <h3 className="text-sm font-semibold text-surface-900 mb-4">Top 5 Most Used</h3>
              <div className="space-y-3">
                {topFeatures.map((f, i) => {
                  const pct = totalFeatureUsage > 0 ? Math.round((f.total_usage / totalFeatureUsage) * 100) : 0;
                  const cfg = CATEGORY_CONFIG[(f.category || '').toLowerCase()];
                  const planDef = planByKey.get(f.feature_key);
                  const isLocked = planDef ? !planDef.is_enabled : false;
                  const planLimit = planDef?.limit_value ?? null;
                  return (
                    <div key={f.feature_key} className={`flex items-center gap-3 ${isLocked ? 'opacity-60' : ''}`}>
                      <span className="text-xs font-bold text-surface-400 w-5 text-right">#{i + 1}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            {isLocked && <Lock size={11} className="text-surface-400 shrink-0" />}
                            <span className="text-sm text-surface-700 truncate">{f.feature_name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${cfg?.color || ''} bg-surface-100`}>
                              {cfg?.label || f.category}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-surface-900 shrink-0">
                            {f.total_usage.toLocaleString()}
                            {planLimit !== null && (
                              <span className="text-surface-500 font-normal"> / {formatLimit(planLimit)}</span>
                            )}
                          </span>
                        </div>
                        <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {topFeatures.length === 0 && (
                  <div className="text-sm text-surface-500 py-4 text-center">No usage data yet</div>
                )}
              </div>
            </div>

            <div className="glass-panel p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle size={14} className="text-surface-400" />
                <h3 className="text-sm font-semibold text-surface-900">Unused Features</h3>
                <span className="ml-auto text-xs text-surface-500">{unusedFeatures.length} of {planSize || featureUsage.length}</span>
              </div>
              {unusedFeatures.length > 0 ? (
                <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin">
                  {unusedFeatures.map(f => {
                    const cfg = CATEGORY_CONFIG[(f.category || '').toLowerCase()];
                    const planDef = planByKey.get(f.feature_key);
                    const isLocked = planDef ? !planDef.is_enabled : false;
                    return (
                      <div key={f.feature_key} className={`flex items-center justify-between py-1.5 border-b border-overlay/[0.04] last:border-0 ${isLocked ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          {isLocked && (
                            <Lock size={11} className="text-surface-400 shrink-0"
                              aria-label="Not included in this plan" />
                          )}
                          <span className="text-sm text-surface-600 truncate">{f.feature_name}</span>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${cfg?.color || 'text-surface-500'} bg-surface-100 shrink-0`}>
                          {cfg?.label || f.category}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-emerald-600 dark:text-emerald-400 py-4 text-center">
                  All features are being used!
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="glass-panel p-5">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 size={16} className="text-brand-500" />
            <h3 className="text-sm font-semibold text-surface-900">Feature Usage</h3>
            <span className="text-xs text-surface-400 ml-2">(populated after the next sync)</span>
          </div>
          <div className="text-sm text-surface-500 text-center py-6">
            No feature usage data available yet — click <strong>Refresh metrics</strong> above or wait for the daily sync.
          </div>
        </div>
      )}

      {/* ── Bulletin Board Interests ────────────────────────────────────── */}
      {interestStats && (interestStats.total_interests > 0 || interestStats.active_bulletins > 0) && (
        <div className="glass-panel p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-surface-900 flex items-center gap-2">
              <Newspaper size={16} className="text-brand-500" />
              Bulletin Board
            </h3>
            {interestStats.recent_interests > 0 && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full badge-emerald border border-emerald-800/40">
                +{interestStats.recent_interests} this week
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-xl bg-surface-100/50">
              <div className="text-2xl font-bold text-surface-900">{interestStats.total_interests}</div>
              <div className="text-xs text-surface-500 mt-0.5">Total Interests</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-surface-100/50">
              <div className="text-2xl font-bold text-surface-900">{interestStats.active_bulletins}</div>
              <div className="text-xs text-surface-500 mt-0.5">Active Listings</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-surface-100/50">
              <div className="text-2xl font-bold text-surface-900">{interestStats.recent_interests}</div>
              <div className="text-xs text-surface-500 mt-0.5">New This Week</div>
            </div>
          </div>
          {interestStats.top_bulletin_title && interestStats.top_bulletin_interests > 0 && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-surface-100/50 rounded-lg">
              <Heart size={14} className="text-rose-400 shrink-0" />
              <span className="text-xs text-surface-500">Most popular:</span>
              <span className="text-xs font-medium text-surface-900 truncate">{interestStats.top_bulletin_title}</span>
              <span className="text-xs text-surface-500 shrink-0">({interestStats.top_bulletin_interests})</span>
            </div>
          )}
        </div>
      )}

      {/* ── Buddy Trips analytics ───────────────────────────────────────── */}
      <BuddyTripsAnalyticsCard tenantId={tenantId} />

      {/* ── Buddy Trips abuse moderation (overview only) ─────────────────
          Hidden when there's no abuse activity so a quiet tenant never
          sees a "0 of 0" panel. Per-user detail (recent strikes, top
          terms, unblock action) lives on the Users tab — the card
          links across via the "Manage in Users" affordance. */}
      <BuddyAbuseAnalyticsCard
        tenantId={tenantId}
        manageHref={`/admin/tenants/${tenantId}/manage/users`}
      />

      {/* My Flights + Buddy Trip Chats are tracked as standard gated
          features. Their usage shows up in the Feature Usage panels
          (sync metrics + Analytics page) alongside Trip Itineraries
          rather than as separate dashboard cards — same surface as
          every other capped feature. */}

      {/* ── AI Calls drilldown ──────────────────────────────────────────── */}
      <AiCallsModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        windowLabel={windowLabel}
        metrics={paddedMetrics}
        aiByFeature={aiByFeature}
        aiErrors={aiErrors}
        aiActiveUsers={aiActiveUsers}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/** Unified KPI card: value + plan limit + utilization bar + delta + sparkline. */
function KpiCard({
  icon, label, value, formatValue,
  limit, formatLimit,
  delta, deltaLabel,
  spark,
  delay = 0,
  subtitle,
  tooltip,
  accent,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  formatValue?: (v: number) => string;
  limit?: number | null;
  formatLimit?: (v: number) => string;
  delta?: number;             // percent change vs prior period
  deltaLabel?: string;
  spark?: number[];
  delay?: number;
  subtitle?: string;
  tooltip?: string;
  accent?: 'brand' | 'violet';
  /** When provided, the card becomes a clickable button that opens a
   *  drilldown modal with detailed charts for the metric. */
  onClick?: () => void;
}) {
  const fmtV = formatValue || ((n: number) => n.toLocaleString());
  const fmtL = formatLimit || formatValue || ((n: number) => n.toLocaleString());
  const hasLimit = typeof limit === 'number' && limit > 0;
  const pct = hasLimit ? Math.min(100, Math.round((value / (limit as number)) * 100)) : 0;
  const isHigh = pct > 80;
  const isOver = pct >= 100;
  const barColor = isOver ? 'bg-red-500' : isHigh ? 'bg-amber-500' : (accent === 'violet' ? 'bg-violet-500' : 'bg-brand-500');
  const sparkColor = accent === 'violet' ? '#8b5cf6' : 'rgb(var(--brand-primary, 42 179 177))';

  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`glass-panel p-5 animate-slide-up opacity-0 transition-all duration-300 hover:shadow-card-hover hover:-translate-y-0.5 text-left w-full ${
        onClick ? 'cursor-pointer' : ''
      }`}
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
      title={tooltip}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-1.5 rounded-lg bg-brand-500/8 text-brand-500">{icon}</div>
        {typeof delta === 'number' && (
          <DeltaBadge delta={delta} />
        )}
      </div>
      <div className="text-2xl font-bold text-surface-900 tracking-tight leading-none">
        {fmtV(value)}
        {hasLimit && (
          <span className="text-sm font-normal text-surface-500"> / {fmtL(limit as number)}</span>
        )}
      </div>
      <div className="text-xs text-surface-500 mt-1 flex items-center gap-1">
        {label}
        {onClick && <span className="text-surface-400" aria-label="Click for details">›</span>}
      </div>

      {hasLimit ? (
        <div className="mt-3 h-1.5 bg-surface-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      ) : spark && spark.length > 1 ? (
        <Sparkline values={spark} color={sparkColor} className="mt-3 h-8 w-full" />
      ) : null}

      {hasLimit && spark && spark.length > 1 && (
        <Sparkline values={spark} color={sparkColor} className="mt-2 h-6 w-full" />
      )}

      {(deltaLabel || subtitle) && (
        <div className="text-[10px] text-surface-400 mt-1.5 truncate">
          {deltaLabel || subtitle}
        </div>
      )}
    </Tag>
  );
}

function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-surface-100 text-surface-600">
        No change
      </span>
    );
  }
  const positive = delta > 0;
  const cls = positive
    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
    : 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400';
  const Icon = positive ? ArrowUp : ArrowDown;
  const display = Math.abs(delta) > 999 ? '>999%' : `${Math.abs(delta)}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cls}`}>
      <Icon size={9} />
      {display}
    </span>
  );
}

/** Tiny SVG sparkline. */
function Sparkline({ values, color, className }: { values: number[]; color: string; className?: string }) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * 100;
      const y = 100 - ((v - min) / range) * 100;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg className={className} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        opacity="0.85"
      />
    </svg>
  );
}

function MiniRow({ icon, label, value }: { icon: ReactNode; label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-surface-500">
        {icon}<span className="text-xs">{label}</span>
      </div>
      <span className="text-sm font-medium text-surface-900">{value}</span>
    </div>
  );
}

/** Window selector — preset toggles + Custom popover. */
function WindowSelector({
  value, onChange,
}: {
  value: WindowSelection;
  onChange: (next: WindowSelection) => void;
}) {
  const presets: PresetDays[] = [7, 14, 30, 90, 365];
  const [customOpen, setCustomOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(
    value.kind === 'custom' ? value.from : isoDate(addDays(new Date(), -30)),
  );
  const [draftTo, setDraftTo] = useState(
    value.kind === 'custom' ? value.to : isoDate(new Date()),
  );

  const isCustom = value.kind === 'custom';

  const apply = () => {
    if (draftFrom && draftTo && draftFrom <= draftTo) {
      onChange({ kind: 'custom', from: draftFrom, to: draftTo });
      setCustomOpen(false);
    }
  };

  return (
    <div className="relative inline-flex items-center rounded-lg bg-surface-100 p-0.5">
      {presets.map(opt => (
        <button
          key={opt}
          onClick={() => onChange({ kind: 'preset', days: opt })}
          className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
            value.kind === 'preset' && value.days === opt
              ? 'bg-surface-0 text-surface-900 shadow-sm'
              : 'text-surface-500 hover:text-surface-700'
          }`}
        >
          {PRESET_LABEL[opt]}
        </button>
      ))}
      <button
        onClick={() => setCustomOpen(o => !o)}
        title={isCustom ? `${value.from} → ${value.to}` : 'Custom date range'}
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
          isCustom
            ? 'bg-surface-0 text-surface-900 shadow-sm'
            : 'text-surface-500 hover:text-surface-700'
        }`}
      >
        <CalendarRange size={12} />
        {isCustom ? `${value.from.slice(5)} → ${value.to.slice(5)}` : 'Custom'}
      </button>

      {customOpen && (
        <div className="absolute right-0 top-full mt-2 z-30 bg-surface-0 border border-surface-200 rounded-xl shadow-lg p-3 w-64">
          <div className="text-xs font-medium text-surface-700 mb-2">Custom range</div>
          <label className="block text-[10px] text-surface-500 mb-1">From</label>
          <input
            type="date"
            value={draftFrom}
            onChange={(e) => setDraftFrom(e.target.value)}
            max={draftTo}
            className="w-full px-2 py-1 text-xs bg-surface-0 text-surface-900 border border-surface-300 rounded-md mb-2"
          />
          <label className="block text-[10px] text-surface-500 mb-1">To</label>
          <input
            type="date"
            value={draftTo}
            onChange={(e) => setDraftTo(e.target.value)}
            min={draftFrom}
            max={isoDate(new Date())}
            className="w-full px-2 py-1 text-xs bg-surface-0 text-surface-900 border border-surface-300 rounded-md mb-3"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setCustomOpen(false)} className="text-xs text-surface-500 hover:text-surface-900">
              Cancel
            </button>
            <button onClick={apply} className="text-xs font-medium px-2.5 py-1 bg-brand-500 text-white rounded-md hover:bg-brand-600">
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Auto-refresh interval picker — Off / 30s / 60s / 5m. */
function AutoRefreshToggle({
  value, onChange,
}: {
  value: AutoRefreshInterval;
  onChange: (next: AutoRefreshInterval) => void;
}) {
  const opts: { v: AutoRefreshInterval; label: string }[] = [
    { v: 0,   label: 'Off' },
    { v: 30,  label: '30s' },
    { v: 60,  label: '1m' },
    { v: 300, label: '5m' },
  ];
  const cur = opts.find(o => o.v === value) || opts[0];
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value) as AutoRefreshInterval)}
      title={`Auto-refresh: ${cur.label}`}
      className="text-xs font-medium px-2 py-1.5 rounded-lg bg-surface-100 hover:bg-surface-200 text-surface-700 cursor-pointer border-0"
    >
      {opts.map(o => (
        <option key={o.v} value={o.v}>Auto: {o.label}</option>
      ))}
    </select>
  );
}
